<template>
  <div>
    <div class="tank-info-grid-container">
      <div class="nested-1">
        <div class="form-row-input" ref="tankTypeRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="tankTypes"
            placeholder="Tank Type: *"
            :fields="fields"
            v-model="tank.tankType"
            @change="tankTypeChange"
            cssClass="required"
          />
          <label class="e-error" v-if="tankTypeMessage">{{ tankTypeMessage }}</label>
        </div>

        <div class="form-row-input" ref="forAstRef" v-if="tank.isInstallationTank">
          <ejs-dropdownlist
            ref="forAstRefDropdown"
            float-label-type="Always"
            :dataSource="forASTsOnly"
            placeholder="For AST's Only: *"
            @change="onChangingAST($event.value)"
            v-model="tank.forASTsOnly"
            :fields="{ value: 'message' }"
            cssClass="required"
          />

          <label class="e-error" v-if="forASTMessage">{{ forASTMessage }}</label>
        </div>

        <div
          class="form-row-input"
          ref="forSingleWallRef"
          v-if="showSingleWall && tank.isInstallationTank"
        >
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="forSingleWall"
            cssClass="required"
            placeholder="Single Wall: *"
            :value="tank.atsSingleWall"
            @change="onATSSingleDoubleChange($event.value, 'atsSingleWall', 'atsDoubleWall')"
            :fields="fields"
          />

          <label class="e-error" v-if="singleWallMessage">{{ singleWallMessage }}</label>
        </div>

        <div
          class="form-row-input"
          ref="forDoubleWallRef"
          v-if="showDoubleWall && tank.isInstallationTank"
        >
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="forDoubleWall"
            cssClass="required"
            placeholder="Secondary Containment: *"
            :value="tank.atsDoubleWall"
            @change="onATSSingleDoubleChange($event.value, 'atsDoubleWall', 'atsSingleWall')"
            :fields="fields"
          />

          <label class="e-error" v-if="doubleWallMessage">{{ doubleWallMessage }}</label>
        </div>

        <div class="form-row-input" ref="forUlcApiRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="constructionType"
            :fields="fields"
            placeholder="ULC/API Dropdown: *"
            :value="tank.constructionType"
            @change="onValueChange($event.value, 'constructionType')"
            cssClass="required"
          />

          <label class="e-error" v-if="ulcApiMessage">{{ ulcApiMessage }}</label>
        </div>

        <div class="form-row-input" ref="forUlcApiDescRef" v-if="constructionTypeDesc">
          <scc-input-validator
            :rules="[requiredRule]"
            :value="tank.constructionDesc"
            :showErrorLabel="showErrorConstructionDesc"
            :errorLabelMessage="constructionDescMessage"
          >
            <ejs-textbox
              float-label-type="Always"
              placeholder="ULC/API Description: *"
              v-model="tank.constructionDesc"
              :maxlength="250"
              @input="
                onInputChange(
                  $event.value,
                  'forUlcApiDescRef',
                  'showErrorConstructionDesc',
                  'constructionDescMessage'
                )
              "
              :multiline="true"
              cssClass="required"
              :row="5"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input">
          <ejs-textbox
            float-label-type="Always"
            placeholder="ASCA Tank Number:"
            v-model="tank.ascaTankNumber"
            class="readOnlyField"
            :readOnly="true"
            :maxlength="250"
          />
        </div>

        <div class="form-row-input" ref="customerTankNumberRef">
          <scc-input-validator
            :rules="[requiredRule]"
            :value="tank.custTankNumber"
            :showErrorLabel="showErrorCustTankNumber"
            :errorLabelMessage="custTankNumberMessage"
          >
            <ejs-textbox
              float-label-type="Always"
              placeholder="Customer Tank Number: *"
              :maxlength="250"
              @input="
                onInputChange(
                  $event.value,
                  'customerTankNumberRef',
                  'showErrorCustTankNumber',
                  'custTankNumberMessage'
                )
              "
              cssClass="required"
              v-model="tank.custTankNumber"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input" ref="tankSerialNumberRef">
          <scc-input-validator
            :rules="[requiredRule]"
            :value="tank.serialNo"
            :showErrorLabel="showErrorSerialNo"
            :errorLabelMessage="serialNoMessage"
          >
            <ejs-textbox
              float-label-type="Always"
              placeholder="Tank Serial Number: *"
              :maxlength="250"
              @input="
                onInputChange(
                  $event.value,
                  'tankSerialNumberRef',
                  'showErrorSerialNo',
                  'serialNoMessage'
                )
              "
              cssClass="required"
              v-model="tank.serialNo"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input" ref="tankCapacityRef">
          <scc-input-validator
            :rules="[requiredRule]"
            :number="tank.capacity"
            :showErrorLabel="showErrorCapacity"
            :errorLabelMessage="capacityMessage"
          >
            <ejs-numerictextbox
              :ref="`tankCapacity${tank.ascaTankNumber}`"
              float-label-type="Always"
              placeholder="Tank Capacity (Litres): *"
              v-model="tank.capacity"
              decimals="0"
              format="#"
              :showSpinButton="false"
              validateDecimalOnType="true"
              :maxlength="250"
              :onInput="onChangingtankCapacity()"
              cssClass="required"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input" ref="tankContentTypeRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="productTypes"
            placeholder="Type of Product Stored: *"
            :fields="fields"
            v-model="tank.contentsType"
            @change="onValueChange($event.value, 'contentsType')"
            cssClass="required"
          />

          <label class="e-error" v-if="contentsTypeMessage">{{ contentsTypeMessage }}</label>
        </div>

        <div class="form-row-input" ref="tankLeakDetectionRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="tankLeakDetection"
            :fields="fields"
            placeholder="Tank Leak Detection: *"
            :value="tank.tankLeakDetection"
            @change="onValueChange($event.value, 'tankLeakDetection')"
            cssClass="required"
          />

          <label class="e-error" v-if="tankLeakDetectionMessage">{{
            tankLeakDetectionMessage
          }}</label>
        </div>

        <div class="form-row-input" ref="tankLeakDetectionDescRef" v-if="tankLeakDetectionDesc">
          <scc-input-validator
            :rules="[requiredRule]"
            :value="tank.tankLeakDetectionDescription"
            :showErrorLabel="showErrorTankLeakDetectionDesc"
            :errorLabelMessage="tankLeakDetectionDescMessage"
          >
            <ejs-textbox
              float-label-type="Always"
              placeholder="Tank Leak Detection Description: *"
              v-model="tank.tankLeakDetectionDescription"
              :maxlength="250"
              @input="
                onInputChange(
                  $event.value,
                  'tankLeakDetectionDescRef',
                  'showErrorTankLeakDetectionDesc',
                  'tankLeakDetectionDescMessage'
                )
              "
              :multiline="true"
              :row="5"
              cssClass="required"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input" ref="tankSpillContainmentSystemRef">
          <ejs-dropdownlist
            float-label-type="Always"
            placeholder="Spill Containment System: *"
            :dataSource="spillContainmentSystem"
            cssClass="required"
            v-model="tank.spillContainmentSystem"
            @change="onValueChange($event.value, 'spillContainmentSystem')"
            :fields="fields"
          />

          <label class="e-error" v-if="spillContainmentSystemMessage">{{
            spillContainmentSystemMessage
          }}</label>
        </div>

        <div class="form-row-input" ref="tankOverfillProtectionRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="overfillProtection"
            placeholder="Overfill Protection: *"
            :value="tank.overfillProtection"
            @change="onValueChange($event.value, 'overfillProtection')"
            :fields="fields"
            cssClass="required"
          />

          <label class="e-error" v-if="overfillProtectionMessage">{{
            overfillProtectionMessage
          }}</label>
        </div>

        <div
          class="form-row-input"
          ref="tankOverfillProtectionDescRef"
          v-if="overfillProtectionDesc"
        >
          <scc-input-validator
            :rules="[requiredRule]"
            :value="tank.overfillProtectionDescription"
            :showErrorLabel="showErrorOverfillProtectionDesc"
            :errorLabelMessage="overfillProtectionDescMessage"
          >
            <ejs-textbox
              float-label-type="Always"
              placeholder="Overfill Protection Description: *"
              v-model="tank.overfillProtectionDescription"
              :maxlength="250"
              @input="
                onInputChange(
                  $event.value,
                  'tankOverfillProtectionDescRef',
                  'showErrorOverfillProtectionDesc',
                  'overfillProtectionDescMessage'
                )
              "
              :multiline="true"
              :row="5"
              cssClass="required"
            />
          </scc-input-validator>
        </div>

        <div class="form-row-input" ref="tankSumpRef">
          <ejs-dropdownlist
            float-label-type="Always"
            :dataSource="sumps"
            :fields="fields"
            placeholder="Sumps: *"
            v-model="tank.sump"
            @change="onValueChange($event.value, 'sump')"
            cssClass="required"
          />

          <label class="e-error" v-if="sumpMessage">{{ sumpMessage }}</label>
        </div>
      </div>

      <div class="nested-2">
        <!-- Checkboxes/Radio buttons -->
        <div
          class="form-row-input"
          ref="tankSplitRef"
          style="display: flex; flex-direction: column"
        >
          <b>Split Tank?: *</b><br />
          <div>
            <ejs-radiobutton
              label="YES"
              :name="`split${tank.ascaTankNumber}`"
              :value="tank.splitType"
              @change="onChangeSplitTank('Yes')"
              :checked="tank.splitType == 'Yes'"
            />
          </div>
          <br />
          <div>
            <ejs-radiobutton
              label="NO"
              :name="`split${tank.ascaTankNumber}`"
              :value="tank.splitType"
              @change="onChangeSplitTank('No')"
              :checked="tank.splitType == 'No'"
            />
          </div>
          <br />
          <label class="e-error" v-if="splitTypeMessage">{{ splitTypeMessage }}</label>
        </div>

        <div class="form-row-input align-tank-info-items span-2">
          <b>Vent Piping:</b>
          <ejs-checkbox
            name="ventPiping"
            :checked="tank.ventPipingCombusible"
            v-model="tank.ventPipingCombusible"
            label="Combustible"
          />
          <ejs-checkbox
            name="ventPiping"
            :checked="tank.ventPipingFlammable"
            v-model="tank.ventPipingFlammable"
            label="Flammable"
          />
          <ejs-checkbox
            name="ventPiping"
            :checked="tank.ventPipingAboveGrade"
            v-model="tank.ventPipingAboveGrade"
            label="Above Grade Collision Protection"
          />
        </div>

        <div class="form-row-input align-tank-info-items">
          <b>Piping:</b>
          <ejs-checkbox
            name="piping"
            :checked="tank.pipingAboveground"
            v-model="tank.pipingAboveground"
            label="Aboveground"
          />
          <ejs-checkbox
            name="piping"
            :checked="tank.pipingBelowground"
            v-model="tank.pipingBelowground"
            label="Underground"
          />
          <ejs-checkbox
            name="piping"
            :checked="tank.pipingNone"
            v-model="tank.pipingNone"
            label="No piping"
          />
        </div>

        <div class="form-row-input align-tank-info-items span-2">
          <b>Piping Containment:</b>
          <ejs-radiobutton
            :name="`pipingContainment${tank.ascaTankNumber}`"
            :checked="tank.pipingContainment == 'Double Wall'"
            @change="tank.pipingContainment = 'Double Wall'"
            label="Double Wall"
          />
          <ejs-radiobutton
            :name="`pipingContainment${tank.ascaTankNumber}`"
            :checked="tank.pipingContainment == 'Single Wall'"
            @change="tank.pipingContainment = 'Single Wall'"
            label="Single Wall"
          />
          <ejs-dropdownlist
            v-model="tank.pipingMaterial"
            :dataSource="pipingMaterial"
            :fields="fields"
            placeholder="Piping Material Type:"
          />
        </div>

        <div class="form-row-input align-tank-info-items span-2">
          <b>Pumping System Type:</b>
          <ejs-radiobutton
            :name="`pumpingSystemType${tank.ascaTankNumber}`"
            :checked="tank.pumpingSystemType == 'Pressure'"
            @change="tank.pumpingSystemType = 'Pressure'"
            label="Pressure"
          />
          <ejs-radiobutton
            :name="`pumpingSystemType${tank.ascaTankNumber}`"
            :checked="tank.pumpingSystemType == 'Siphon'"
            @change="tank.pumpingSystemType = 'Siphon'"
            label="Siphon"
          />
          <ejs-radiobutton
            :name="`pumpingSystemType${tank.ascaTankNumber}`"
            :checked="tank.pumpingSystemType == 'Suction'"
            @change="tank.pumpingSystemType = 'Suction'"
            label="Suction"
          />
          <div class="form-row-input">
            <ejs-dropdownlist
              :dataSource="pipingLeakDetection"
              :fields="fields"
              placeholder="Piping Leak Detection: "
              @change="onValueChange($event.value, 'pipingLeakDetection')"
              v-model="tank.pipingLeakDetection"
            />
          </div>

          <div class="form-row-input" v-if="pipingLeakDetectionDesc">
            <ejs-textbox
              float-label-type="Always"
              placeholder="Piping Leak Detection Description:"
              v-model="tank.pipingLeakDetectionDescription"
              :multiline="true"
              :maxlength="250"
              :row="5"
            />
          </div>
        </div>
      </div>
      <div>
        <div class="span" style="display: flex; margin: -20px">
          <ejs-button
            type="button"
            cssClass="e-warning margin-button"
            v-on:click.native="deleteTank()"
          >
            <font-awesome-icon :icon="['fas', 'trash-alt']" />&nbsp;Delete
          </ejs-button>
          <!-- <ejs-button
            type="button"
            cssClass="e-success margin-button"
            v-on:click.native="copyToNewTank"
          >
            <font-awesome-icon :icon="['fas', 'file']" />&nbsp;Copy To New
          </ejs-button> -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getForASTsOnly,
  getOverfillProtection,
  getPipingLeakDetection,
  getPipingMaterial,
  getSpillContainmentSystem,
  getSump,
  getTankLeakDetection,
} from "../services/permitting-api";

import sccInputValidator from "../../../components/base/scc-input-validator.vue";
import { requiredRule } from "../../../shared/constants";

export default {
  components: { sccInputValidator },
  name: "InstallationTankInfo",
  props: {
    tank: Object,
    tankIndex: Number,
    forDoubleWall: Array,
    forSingleWall: Array,
    tankTypes: Array,
    constructionType: Array,
    productTypes: Array,
  },
  data() {
    return {
      fields: { value: "message" },
      forASTsOnly: null,
      showSingleWall: false,
      showDoubleWall: false,
      tankLeakDetection: null,
      pipingLeakDetection: null,
      sumps: null,
      constructionTypeDesc: false,
      tankLeakDetectionDesc: false,
      overfillProtectionDesc: false,
      pipingLeakDetectionDesc: false,
      overfillProtection: null,
      pipingMaterial: null,
      spillContainmentSystem: null,
      requiredRule,
      forASTMessage: null,
      tankTypeMessage: null,
      singleWallMessage: null,
      doubleWallMessage: null,
      ulcApiMessage: null,
      constructionDescMessage: null,
      showErrorConstructionDesc: false,
      custTankNumberMessage: null,
      showErrorCustTankNumber: false,
      serialNoMessage: null,
      showErrorSerialNo: false,
      capacityMessage: null,
      showErrorCapacity: false,
      contentsTypeMessage: null,
      tankLeakDetectionMessage: null,
      tankLeakDetectionDescMessage: null,
      showErrorTankLeakDetectionDesc: false,
      spillContainmentSystemMessage: null,
      overfillProtectionMessage: null,
      overfillProtectionDescMessage: null,
      showErrorOverfillProtectionDesc: false,
      sumpMessage: null,
      splitTypeMessage: null,
      atsSingalWall: null,
    };
  },
  computed: {
    ...mapGetters({
      permit: "permit",
    }),
  },
  async created() {
    // eslint-disable-next-line no-return-assign
    await getForASTsOnly().then((res) => (this.forASTsOnly = res.data));
    // eslint-disable-next-line no-return-assign
    await getPipingMaterial().then((res) => (this.pipingMaterial = res.data));
    // eslint-disable-next-line no-return-assign
    await getPipingLeakDetection().then((res) => (this.pipingLeakDetection = res.data));
    // eslint-disable-next-line no-return-assign
    await getSpillContainmentSystem().then((res) => (this.spillContainmentSystem = res.data));
    // eslint-disable-next-line no-return-assign
    await getTankLeakDetection().then((res) => (this.tankLeakDetection = res.data));
    // eslint-disable-next-line no-return-assign
    await getOverfillProtection().then((res) => (this.overfillProtection = res.data));
    // eslint-disable-next-line no-return-assign
    await getSump().then((res) => (this.sumps = res.data));
    // eslint-disable-next-line no-return-assign
    await getPipingLeakDetection().then((res) => (this.pipingLeakDetection = res.data));
    this.showDoubleWall = this.forDoubleWall?.find((x) => x.message === this.tank.atsDoubleWall) || this.tank.forASTsOnly === 'Double Wall';
    this.showSingleWall = this.forSingleWall?.find((x) => x.message === this.tank.atsSingleWall) || this.tank.forASTsOnly === 'Single Wall';
    this.constructionTypeDesc = this.showDesc(this.tank.constructionType);
    this.tankLeakDetectionDesc = this.showDesc(this.tank.tankLeakDetection);
    this.overfillProtectionDesc = this.showDesc(this.tank.overfillProtection);
    this.pipingLeakDetectionDesc = this.showDesc(this.tank.pipingLeakDetection);
  },
  methods: {
    deleteTank() {
      const { tankIndex } = this;
      const { tank } = this;
      this.$emit("deleteTank", { tankIndex, tank });
    },
    tankTypeChange(e) {
      if (e?.value) {
        this.$refs.tankTypeRef.classList.remove("e-error");
        this.tankTypeMessage = null;
      }
    },
    // copyToNewTank() {
    //   let copiedTank = { ...this.tank };
    //   delete copiedTank.eSiteId;
    //   delete copiedTank.eTankId;
    //   this.$emit("copyToNewTank", copiedTank);
    // },
    onChangingAST(val) {
      if (val === "Single Wall") {
        this.showSingleWall = true;
        this.showDoubleWall = false;
      } else {
        this.showDoubleWall = true;
        this.showSingleWall = false;
      }
      this.$refs.forAstRef?.classList.remove("e-error");
      this.forASTMessage = null;
    },
    onATSSingleDoubleChange(val, propertyToUpdate, propertyToRemove) {
      if (propertyToUpdate === "atsSingleWall") {
        this.$refs.forSingleWallRef?.classList.remove("e-error");
        this.singleWallMessage = null;
      } else {
        this.$refs.forDoubleWallRef?.classList.remove("e-error");
        this.doubleWallMessage = null;
      }
      this.tank[propertyToUpdate] = val;
      this.tank[propertyToRemove] = null;
    },
    onInputChange(val, ref, showError, message) {
      if (val) {
        this[message] = null;
        this[showError] = false;
        this.$refs[ref]?.classList.remove("e-error");
      }
    },
    showDesc(val) {
      return val?.toLowerCase().includes("other");
    },
    onValueChange(val, prop) {
      let refs = null;
      // eslint-disable-next-line default-case
      switch (prop) {
        case "constructionType":
          refs = "forUlcApiRef";
          this.ulcApiMessage = null;
          break;
        case "tankLeakDetection":
          refs = "tankLeakDetectionRef";
          this.tankLeakDetectionMessage = null;
          break;
        case "overfillProtection":
          refs = "tankOverfillProtectionRef";
          this.overfillProtectionMessage = null;
          break;
        case "contentsType":
          refs = "tankContentTypeRef";
          this.contentsTypeMessage = null;
          break;
        case "spillContainmentSystem":
          refs = "tankSpillContainmentSystemRef";
          this.spillContainmentSystemMessage = null;
          break;
        case "sump":
          refs = "tankSumpRef";
          this.sumpMessage = null;
          break;
      }
      this.$refs[refs]?.classList.remove("e-error");
      const showDescription = this.showDesc(val);
      this[`${prop}Desc`] = showDescription;
      this.tank[prop] = val;
    },
    onChangeSplitTank(val) {
      if (val === "Yes") {
        this.$parent.$refs.footerDialog.show();
      }
      this.$refs.tankSplitRef?.classList.remove("e-error");
      this.splitTypeMessage = null;
      this.tank.splitType = val;
    },
    onChangingtankCapacity() {
      let ref = null;
      if (this?.$refs) ref = this.$refs[`tankCapacity${this.tank.ascaTankNumber}`];
      if (ref?.$el.value) {
        this.$refs.tankCapacityRef.classList.remove("e-error");
        this.capacityMessage = null;
        this.showErrorCapacity = false;
      }
    },
  },
};
</script>
